import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../utils/apiClient';
import { JourneyListItem } from '../../../services/investor.service';
import { setSAProxyJourneyId } from '../../../services/saProxyAdapter';
import { investorService } from '../../../services/investor.service';
import { useDelegationPermissions } from '../../../contexts/DelegationPermissionsContext';

/**
 * B1 — Service-Agent journey picker. After opening an investor, the agent chooses which product
 * journey to work within. Selecting one sets the proxy journey scope ({@link setSAProxyJourneyId}) so
 * every subsequent proxied call is tagged with `journeyId` and the backend enforces the per-product
 * delegation wall (Stage B). "Profile & KYC" is identity-level (shared across products) and clears the
 * journey scope.
 */
export const SAJourneySelect: React.FC = () => {
  const { investorId } = useParams<{ investorId: string }>();
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState<JourneyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const delegationPerms = useDelegationPermissions();

  useEffect(() => {
    // Choosing — not yet inside a journey. Reset any prior journey scope.
    setSAProxyJourneyId(null);
    (async () => {
      try {
        const res = await apiClient.get<JourneyListItem[]>(
          `/api/service-agents/me/investors/${investorId}/journeys`
        );
        setJourneys(res.data || []);
      } catch {
        setJourneys([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [investorId]);

  const openJourney = async (j: JourneyListItem) => {
    setSAProxyJourneyId(j.journeyId || null);
    // Enter the investor workspace scoped to this journey — every proxied call now carries the
    // journeyId, so profile/KYC/onboarding/documents are all fetched + access-checked per journey.
    const profile = `/service-agent/investors/${investorId}/profile`;
    // UAT 20: the same first step the investor gets — KYC not done and Smart Upload not
    // skipped opens Smart Upload first. Only for agents who can upload (Onboarding Only,
    // Full Onboarding); the SOW gate is left to the Information page, which shows the
    // waiting notice. Any failure here falls through to the Information tabs as before.
    if (j.journeyId && delegationPerms.canUploadDocuments) {
      try {
        const gate = await investorService.getJourneyKycGate(j.journeyId);
        if (gate.sowAgreed && !gate.kycComplete && !gate.smartUploadSkipped) {
          navigate(`/service-agent/investors/${investorId}/documents-center?journeyId=${encodeURIComponent(j.journeyId)}&from=journey`);
          return;
        }
      } catch (err) {
        console.warn('[SAJourneySelect] KYC gate unavailable, opening the Information tabs', err);
      }
    }
    navigate(profile);
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 12 }}>
      <div className="mb-3">
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Select a journey to work on</h3>
        <div className="small text-muted">Each product/route is a separate journey. All data and access are scoped to the journey you open.</div>
      </div>

      {loading ? (
        <div className="text-muted small">Loading journeys…</div>
      ) : journeys.length === 0 ? (
        <div className="text-muted small">No product journeys found for this investor.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {journeys.map((j, idx) => (
            <div key={j.journeyId || idx} className="border rounded p-2 d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold text-dark">{j.product || 'Product'}</div>
                <div className="small text-muted">
                  {j.serviceProviderName || '—'}{j.plan ? ` · ${j.plan}` : ''} · {j.status || 'IN PROGRESS'}
                </div>
              </div>
              <button onClick={() => openJourney(j)} className="btn btn-sm btn-primary">Open journey</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SAJourneySelect;
